import React, { useState, useEffect, useContext, useMemo } from 'react';
import FullScreenPanel from '../components/UI/FullScreenPanel';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import axios from 'axios';
import { apiUrl } from '../config/api';
import ResponsiveTable from '../components/ResponsiveTable';
import StatusBadge from '../components/UI/StatusBadge';
import { useTableSort } from '../components/UI/tableSort';
import ActionButton from '../components/UI/ActionButton';
import KpiCard from '../components/Admin/KpiCard';
import { rolesMetrics } from '../utils/adminMetrics';
import { exportToCsv } from '../utils/adminExport';
import { auditModalView } from '../utils/auditClient';
import Pager from '../components/UI/Pager';

const PAGE_SIZE = 10;

const ManageRoles = () => {
  const [roles, setRoles] = useState([]);
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const hasManageConfig = Array.isArray(user?.permissions) && user.permissions.includes('ManageConfig');
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', is_active: true });
  const [message, setMessage] = useState('');
  const [formErrors, setFormErrors] = useState({ name: '' });
  const [confirmAction, setConfirmAction] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Inline permissions assignment modal state
  // Inline card-based permission assignment (no modal)
  const [permModules, setPermModules] = useState([]); // list of module names
  const [permMap, setPermMap] = useState({}); // { module_name: boolean }
  const [permLoading, setPermLoading] = useState(false);
  const [permMessage, setPermMessage] = useState('');
  const [newModuleName, setNewModuleName] = useState('');
  const [permSearch, setPermSearch] = useState('');
  const [permCopyRoleId, setPermCopyRoleId] = useState('');
  const [expandedRoleId, setExpandedRoleId] = useState(null);

  // Global create-permission modal
  const [showCreatePermModal, setShowCreatePermModal] = useState(false);
  const [globalNewModuleName, setGlobalNewModuleName] = useState('');

  // Derive groups from module names for better readability
  const deriveGroup = (mod) => {
    if (!mod) return 'General';
    const m = String(mod);
    if (/report/i.test(m)) return 'Reports';
    if (/config|setting/i.test(m)) return 'Configuration';
    if (/loan/i.test(m)) return 'Loans';
    if (/client/i.test(m)) return 'Clients';
    if (/user/i.test(m)) return 'Users';
    if (/role/i.test(m)) return 'Roles';
    if (/manage/i.test(m)) return 'Management';
    return 'General';
  };

  const filteredModules = useMemo(() => {
    const q = (permSearch || '').toLowerCase().trim();
    if (!q) return permModules;
    return permModules.filter(m => m.toLowerCase().includes(q));
  }, [permModules, permSearch]);

  const groupedModules = useMemo(() => {
    const map = {};
    filteredModules.forEach(m => {
      const g = deriveGroup(m);
      if (!map[g]) map[g] = [];
      map[g].push(m);
    });
    // Sort groups and modules
    const sorted = Object.fromEntries(
      Object.entries(map)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([g, mods]) => [g, mods.sort((a, b) => a.localeCompare(b))])
    );
    return sorted;
  }, [filteredModules]);

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(apiUrl('/api/roles'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRoles(response.data);
      setError(null);
    } catch (err) {
  const status = err.response?.status;
  if (status === 401) setError('Unauthorized: please log in');
  else if (status === 403) setError('Access denied: insufficient permissions');
  else if (status === 404) setError('Roles endpoint not found (404)');
  else setError(`Error fetching roles: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleExport = () => {
    const rows = filteredRoles.map(r => ({
      ID: r.id,
      Name: r.name,
      Description: r.description || '',
      Active: r.is_active ? 'Yes' : 'No'
    }));
    exportToCsv('roles.csv', rows);
  };

  const validateForm = () => {
    let valid = true;
    const errors = { name: '' };
    if (!formData.name.trim()) {
      errors.name = 'Role name is required';
      valid = false;
    }
    setFormErrors(errors);
    return valid;
  };

  const handleAddRole = async () => {
    if (!validateForm()) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(apiUrl('/api/roles'), formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShowModal(false);
      setFormData({ name: '', description: '', is_active: true });
      setFormErrors({ name: '' });
      setMessage('Role added successfully');
      fetchRoles();
    } catch (err) {
      const errorResponse = err.response?.data;
      let errorMsg = 'Registration failed';
      if (errorResponse?.error?.includes('duplicate key value violates unique constraint')) {
        errorMsg = 'Role name already exists';
      } else if (errorResponse?.error) {
        errorMsg = errorResponse.error;
      } else if (err.response?.status === 404) {
        errorMsg = 'Roles endpoint not found';
      }
      setMessage(errorMsg);
    }
  };

  const handleUpdateRole = async () => {
    if (!validateForm()) return;
    try {
      const token = localStorage.getItem('token');
      await axios.put(apiUrl(`/api/roles/${selectedRole.id}`), formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShowModal(false);
      setFormData({ name: '', description: '', is_active: true });
      setFormErrors({ name: '' });
      setMessage('Role updated successfully');
      fetchRoles();
    } catch (err) {
      setMessage(err.response?.data?.error || 'Error updating role');
    }
  };

  const handleToggleRole = async (roleId, action) => {
    try {
      const token = localStorage.getItem('token');
  // Use full API path for clarity
  const endpoint = action === 'disable' ? `/api/roles/${roleId}/disable` : `/api/roles/${roleId}/enable`;
  await axios.put(apiUrl(endpoint), {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessage(`${action.charAt(0).toUpperCase() + action.slice(1)} successful`);
      setConfirmAction(null);
      fetchRoles();
    } catch (err) {
  const status = err.response?.status;
  if (status === 401) setMessage('Unauthorized: please log in');
  else if (status === 403) setMessage('Access denied');
  else setMessage(err.response?.data?.error || `Error ${action} role`);
    }
  };

  const requestRoleToggle = (roleId, action, name) => {
    setConfirmAction({ roleId, action, name });
  };

  const handleEditRole = (role) => {
    openModal(role);
  };

  const handleToggleRoleStatus = (role) => {
    requestRoleToggle(role.id, role.is_active ? 'disable' : 'enable', role.name);
  };

  // Open inline permission assignment for a role
  const openPermissionsForRole = async (role) => {
    try {
      if (expandedRoleId === role.id) {
        // collapse
        setExpandedRoleId(null);
        setSelectedRole(null);
        setPermModules([]);
        setPermMap({});
        setPermMessage('');
        setPermSearch('');
        setPermCopyRoleId('');
        return;
      }
      setSelectedRole(role);
      setExpandedRoleId(role.id);
      setPermMessage('');
      setPermLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      // Load modules and role map in parallel
      const [mRes, mapRes] = await Promise.all([
        axios.get(apiUrl('/api/permissions/modules'), { headers }),
        axios.get(apiUrl(`/api/permissions/role/${role.id}`), { headers })
      ]);
      setPermModules(Array.isArray(mRes.data) ? mRes.data : []);
      setPermMap(mapRes.data || {});
    } catch (e) {
      setPermMessage(e?.response?.data?.error || 'Failed to load permissions for this role');
    } finally {
      setPermLoading(false);
    }
  };

  const toggleModule = (mod) => setPermMap(prev => ({ ...prev, [mod]: !prev[mod] }));
  const selectAllModules = (value) => {
    const next = {};
    permModules.forEach(m => { next[m] = !!value; });
    setPermMap(next);
  };
  const saveRolePermissions = async () => {
    if (!selectedRole) return;
    try {
      setPermLoading(true);
      const token = localStorage.getItem('token');
      await axios.put(apiUrl(`/api/permissions/role/${selectedRole.id}`), { permissions: permMap }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPermMessage('Permissions updated');
    } catch (e) {
      setPermMessage(e?.response?.data?.error || 'Failed to update permissions');
    } finally {
      setPermLoading(false);
    }
  };
  const copyPermissionsFromRole = async (sourceRoleId) => {
    if (!sourceRoleId || !selectedRole) return;
    try {
      setPermLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(apiUrl(`/api/permissions/role/${sourceRoleId}`), { headers });
      const src = res.data || {};
      // Normalize to current module registry
      const next = {};
      permModules.forEach(m => { next[m] = !!src[m]; });
      setPermMap(next);
      setPermMessage('Copied from selected role (not saved yet)');
    } catch (e) {
      setPermMessage(e?.response?.data?.error || 'Failed to copy permissions');
    } finally {
      setPermLoading(false);
    }
  };
  const createPermissionModule = async () => {
    const mod = (newModuleName || '').trim();
    if (!mod) { setPermMessage('Module name is required'); return; }
    try {
      setPermLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(
        apiUrl('/api/permissions/create'),
        { module_name: mod, assign_role_id: selectedRole?.id, assign_can_access: true },
        { headers }
      );

      // Refresh modules + role map so the new module appears and is assigned
      const [mRes, mapRes] = await Promise.all([
        axios.get(apiUrl('/api/permissions/modules'), { headers }),
        selectedRole?.id ? axios.get(apiUrl(`/api/permissions/role/${selectedRole.id}`), { headers }) : Promise.resolve({ data: {} })
      ]);
      const list = Array.isArray(mRes.data) ? mRes.data : [];
      setPermModules(list);
      setPermMap(mapRes.data || {});
      setNewModuleName('');
      setPermMessage('Permission module created and assigned to this role');
    } catch (e) {
      setPermMessage(e?.response?.data?.error || 'Failed to create permission module');
    } finally {
      setPermLoading(false);
    }
  };

  const createPermissionModuleGlobal = async () => {
    const mod = (globalNewModuleName || '').trim();
    if (!mod) {
      setMessage('Permission name is required');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(apiUrl('/api/permissions/create'), { module_name: mod }, { headers });

      // Refresh the module registry
      const mRes = await axios.get(apiUrl('/api/permissions/modules'), { headers });
      const list = Array.isArray(mRes.data) ? mRes.data : [];
      setPermModules(list);

      // If a role is currently expanded, refresh its map so the new module appears immediately
      if (selectedRole?.id) {
        try {
          const mapRes = await axios.get(apiUrl(`/api/permissions/role/${selectedRole.id}`), { headers });
          setPermMap(mapRes.data || {});
        } catch (e) {
          // Best-effort; keep UI usable
        }
      }

      setShowCreatePermModal(false);
      setGlobalNewModuleName('');
      setMessage(res?.data?.message || 'Permission module created');
    } catch (e) {
      setMessage(e?.response?.data?.error || 'Failed to create permission module');
    }
  };

  const openModal = (role = null) => {
    setSelectedRole(role);
    setFormData(role || { name: '', description: '', is_active: true });
    setMessage('');
    setFormErrors({ name: '' });
    setShowModal(true);
    auditModalView(role ? 'Edit Role' : 'Add Role');
  };

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  useEffect(() => {
    setCurrentPage(1); // Reset to first page on search
  }, [searchTerm]);

  const getMessageClass = () => {
    if (message.includes('Error') || message === 'Role name already exists' || message === 'Registration failed') {
      return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300';
    } else if (message.includes('successful')) {
      return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300';
    }
    return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300';
  };

  // Search filter
  const filteredRoles = roles.filter(role =>
    role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (role.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (role.is_active ? 'active' : 'inactive').includes(searchTerm.toLowerCase())
  );

  // Pagination logic
  const totalPages = Math.ceil(filteredRoles.length / PAGE_SIZE);
  // The table's rows for every match, so a click on a column sorts them all
  // before the page is taken.
  const roleRows = filteredRoles.map(role => ({
    id: role.id,
    name: role.name,
    description: role.description || 'No description',
    status: role.is_active ? 'Active' : 'Inactive',
    roleData: role,
  }));
  const roleSort = useTableSort(roleRows);
  const paginatedRoles = roleSort.rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="max-w-full min-h-screen">

      {/* KPI Row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 mb-2">
        {(() => {
          const m = rolesMetrics(roles);
          return (
            <>
              <KpiCard label="Total Roles" value={m.total} color="gray" />
              <KpiCard label="Active Roles" value={m.active} color="green" />
              <KpiCard label="Inactive Roles" value={m.inactive} color={m.inactive>0?'red':'green'} />
            </>
          );
        })()}
      </div>

      {error && <div className="p-2 rounded-lg mb-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">{error}</div>}
      {message && <div className={`p-2 rounded-lg mb-2 flex items-center justify-between ${getMessageClass()}`}>
        <span>{message}</span>
        <button
          onClick={() => setMessage('')}
          className="ml-4 px-2 py-1 rounded-full text-lg font-bold hover:bg-gray-200 dark:hover:bg-slate-600 focus:outline-none"
          aria-label="Close"
          title="Close"
        >
          &times;
        </button>
      </div>}

      {confirmAction && (
        <div className="flex items-center justify-between bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 p-2 rounded mb-4 shadow border border-yellow-200 dark:border-yellow-700/50">
          <span>
            Are you sure you want to <strong>{confirmAction.action}</strong> the role{' '}
            <strong>{confirmAction.name}</strong>?
          </span>
          <div className="flex gap-2">
            <ActionButton
              onClick={() => handleToggleRole(confirmAction.roleId, confirmAction.action)}
              className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
              busyLabel="Working…"
            >
              Confirm
            </ActionButton>
            <button
              onClick={() => setConfirmAction(null)}
              className="bg-gray-300 text-gray-800 px-3 py-1 rounded hover:bg-gray-400"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {!(showModal || showCreatePermModal) && (
      <>
      <div className="mb-2 flex flex-col sm:flex-row justify-between items-center gap-2">
        {/* Search on the left */}
        <input
          type="text"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Search roles..."
          className="p-2 w-full sm:max-w-xs"
        />
        {/* Add button, export, and pagination side by side on the right */}
        <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
          {hasManageConfig && (
          <button onClick={() => openModal()} className="px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition font-semibold flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            Add Role
          </button>
          )}
          {hasManageConfig && (
            <button
              onClick={() => { setShowCreatePermModal(true); setGlobalNewModuleName(''); setMessage(''); auditModalView('Create Permission Module'); }}
              className="px-4 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition font-semibold"
              title="Create a new permission module"
              type="button"
            >
              Add Permission
            </button>
          )}
          {hasManageConfig && (
            <button
              onClick={handleExport}
              className="px-4 py-2 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-200 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-600 transition font-semibold"
            >
              Export CSV
            </button>
          )}
          <Pager page={currentPage} pages={totalPages} onPage={setCurrentPage} />
        </div>
      </div>

      {/* Roles list - shared ResponsiveTable renders cards at all breakpoints */}
      <div className="bg-white dark:bg-slate-900">
        <ResponsiveTable
          data={paginatedRoles}
          sort={roleSort.sort}
          onSortChange={roleSort.onSortChange}
          columns={[
            { field: 'name', header: 'Role Name' },
            { field: 'description', header: 'Description' },
            { field: 'status', header: 'Status', formatter: value => (
                <StatusBadge status={value} />
              ) },
            { field: 'actions', header: 'Actions', formatter: (_, row) => (
                <div className="flex flex-wrap items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  {hasManageConfig && (
                    <ActionButton
                      type="button"
                      onClick={(e) => { e.stopPropagation(); return openPermissionsForRole(row.roleData); }}
                      className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300"
                    >
                      {expandedRoleId === row.roleData.id ? 'Hide Permissions' : 'Assign Permissions'}
                    </ActionButton>
                  )}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleEditRole(row.roleData); }}
                    className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-emerald-50 text-emerald-600 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleToggleRoleStatus(row.roleData); }}
                    className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                      row.roleData.is_active
                        ? 'bg-rose-50 text-rose-600 hover:bg-rose-100 dark:bg-rose-900/30 dark:text-rose-300'
                        : 'bg-amber-50 text-amber-600 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-300'
                    }`}
                  >
                    {row.roleData.is_active ? 'Disable' : 'Enable'}
                  </button>
                </div>
              ) },
          ]}
          emptyMessage="No roles found."
        />
      </div>

      {expandedRoleId && (() => {
        const role = roles.find(r => r.id === expandedRoleId);
        if (!role) return null;
        return (
              <FullScreenPanel
                isOpen={true}
                onClose={() => setExpandedRoleId(null)}
                title={`Assign permissions — ${role.name}`}
                subtitle={role.description || undefined}
              >
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <input
                    value={permSearch}
                    onChange={(e) => setPermSearch(e.target.value)}
                    placeholder="Search modules..."
                    className="w-56"
                  />
                  {hasManageConfig && (
                    <>
                      <button onClick={() => selectAllModules(true)} className="px-3 py-2 border border-gray-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600 text-gray-700 dark:text-slate-200">Select All</button>
                      <button onClick={() => selectAllModules(false)} className="px-3 py-2 border border-gray-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600 text-gray-700 dark:text-slate-200">Select None</button>
                      <div className="flex items-center gap-2 ml-auto">
                        <label className="text-sm font-medium text-gray-800 dark:text-slate-200">Copy from role:</label>
                        <select
                          value={permCopyRoleId}
                          onChange={(e) => { setPermCopyRoleId(e.target.value); if (e.target.value) copyPermissionsFromRole(e.target.value); }}
                          className="min-w-[180px]"
                        >
                          <option value="">-- Select Role --</option>
                          {roles.filter(r => r.id !== role.id).map(r => (
                            <option key={r.id} value={r.id}>{r.name}</option>
                          ))}
                        </select>
                        <input
                          value={newModuleName}
                          onChange={(e) => setNewModuleName(e.target.value)}
                          placeholder="Add module (e.g. ManageLoans)"
                          className="w-56"
                        />
                        <ActionButton onClick={createPermissionModule} className="px-3 py-2 bg-green-600 text-white rounded" busyLabel="Adding…">Add</ActionButton>
                        <ActionButton onClick={saveRolePermissions} className="px-3 py-2 bg-blue-600 text-white rounded font-semibold" busyLabel="Saving…">Save</ActionButton>
                      </div>
                    </>
                  )}
                </div>
                {permMessage && (
                  <div className="mb-2 p-2 rounded bg-blue-50 text-blue-700 text-sm">{permMessage}</div>
                )}

                {/* Grouped module cards */}
                {Object.keys(groupedModules).length === 0 ? (
                  <div className="p-4 text-center text-gray-500 dark:text-slate-400 border border-gray-200 dark:border-slate-700 rounded">No modules match your filter.</div>
                ) : (
                  <div className="space-y-3">
                    {Object.entries(groupedModules).map(([group, mods]) => {
                      const total = mods.length;
                      const selectedCount = mods.reduce((s, m) => s + (permMap[m] ? 1 : 0), 0);
                      return (
                        <div key={group}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="font-semibold text-gray-800 dark:text-slate-200">
                              {group} <span className="text-xs text-gray-600 dark:text-slate-400">({selectedCount}/{total})</span>
                            </div>
                            {hasManageConfig && (
                              <div className="flex gap-2">
                                <button onClick={() => { const next = { ...permMap }; mods.forEach(m => { next[m] = true; }); setPermMap(next); }} className="px-2 py-1 text-xs border border-gray-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600 text-gray-700 dark:text-slate-200">Select All</button>
                                <button onClick={() => { const next = { ...permMap }; mods.forEach(m => { next[m] = false; }); setPermMap(next); }} className="px-2 py-1 text-xs border border-gray-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600 text-gray-700 dark:text-slate-200">Select None</button>
                              </div>
                            )}
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                            {mods.map((mod) => (
                              <div key={mod} className="p-3 border border-gray-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 shadow-sm flex items-center justify-between hover:shadow transition">
                                <div className="text-gray-800 dark:text-slate-200 text-sm font-medium truncate" title={mod}>{mod}</div>
                                <input
                                  type="checkbox"
                                  className="w-5 h-5 accent-blue-600"
                                  disabled={permLoading || !hasManageConfig}
                                  checked={!!permMap[mod]}
                                  onChange={() => toggleModule(mod)}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </FullScreenPanel>
        );
      })()}
      </>
      )}

      {showModal && (
        <FullScreenPanel
          isOpen
          onClose={() => setShowModal(false)}
          title={selectedRole ? 'Edit Role' : 'Add Role'}
          footer={(
            <>
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-slate-200 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-600 border border-gray-200 dark:border-slate-600 transition font-medium"
              >
                Cancel
              </button>
              <ActionButton
                onClick={selectedRole ? handleUpdateRole : handleAddRole}
                className="px-4 py-2 text-sm bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 border border-indigo-200 transition font-medium"
                busyLabel="Saving…"
              >
                {selectedRole ? 'Update' : 'Add'}
              </ActionButton>
            </>
          )}
        >
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Role Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Role Name"
                  className={`w-full ${(formData.name || '').trim() ? '' : 'border-rose-300 dark:border-rose-700/60 focus:border-rose-500 focus:ring-rose-500/20'}`}
                  required
                />
                {formErrors.name && <p className="text-rose-500 text-xs mt-1">{formErrors.name}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Description</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of this role"
                  className="w-full"
                />
              </div>
            </div>
        </FullScreenPanel>
      )}

      {showCreatePermModal && hasManageConfig && (
        <FullScreenPanel
          isOpen
          onClose={() => { setShowCreatePermModal(false); setGlobalNewModuleName(''); }}
          title="Add Permission"
          footer={(
            <>
              <button
                type="button"
                onClick={() => { setShowCreatePermModal(false); setGlobalNewModuleName(''); }}
                className="px-4 py-2 text-sm bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-slate-200 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-600 border border-gray-200 dark:border-slate-600 transition font-medium"
              >
                Cancel
              </button>
              <ActionButton
                type="button"
                onClick={createPermissionModuleGlobal}
                className="px-4 py-2 text-sm bg-green-50 text-green-700 rounded-lg hover:bg-green-100 border border-green-200 transition font-medium"
                busyLabel="Creating…"
              >
                Create
              </ActionButton>
            </>
          )}
        >
            <div>
              <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Permission Name</label>
              <input
                type="text"
                value={globalNewModuleName}
                onChange={(e) => setGlobalNewModuleName(e.target.value)}
                placeholder="Permission name (e.g. ManagePermissions)"
                className={`w-full ${globalNewModuleName.trim() ? '' : 'border-rose-300 dark:border-rose-700/60 focus:border-rose-500 focus:ring-rose-500/20'}`}
              />
              <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">Tip: names containing "Loan", "Client", "Config", "User" will auto-group like the sections shown.</p>
              <p className="text-xs text-gray-500 dark:text-slate-400 mt-1.5">Tip: names containing "Loan", "Client", "Config", "User" will auto-group like the sections shown.</p>
            </div>
        </FullScreenPanel>
      )}

      {/* No modal; permissions shown inline per role card */}
    </div>
  );
};

export default ManageRoles;